import { BULK_ACTION_BATCH_SIZE, type Comic } from '@tanko/shared'
import type { EditableField } from './title-rename'
import { CloudApiClient, type CloudCategory } from './api-client'
import { mirrorAssignmentsBatch, mirrorCategories, mirrorComicCategories, mirrorComicMetadata } from '../db/repo'
import { isRegistered } from './comic-sync'
import { errorText, type BulkOutcome } from './bulk-progress'

/**
 * 分類的唯一寫入入口。
 *
 * 真相來源是雲端目錄：每個變更先呼叫 API，成功之後才更新本地鏡像。失敗時鏡像
 * 原封不動，因此鏡像不可能與雲端分歧。代價是離線或認證失效時無法變更分類——
 * 換到的是不需要維護一套離線變更佇列與其衝突解決。
 */

const api = new CloudApiClient()
type CategoryClient = Pick<CloudApiClient,
  'categories' | 'createCategory' | 'renameCategory' | 'deleteCategory' | 'comic' | 'updateComic'
  | 'bulkAssignCategories' | 'bulkEdit'>
/** 只需要讀取與同步鏡像的呼叫端（例如遷移）用這個較窄的介面。 */
export type CategoryReader = Pick<CloudApiClient, 'categories'>

/** 未註冊的作品在雲端目錄沒有對應，分類無處可存。 */
export function catalogIdOf(comic: Comic): string {
  if (!isRegistered(comic) || !comic.cloudComicId) {
    throw new Error(`「${comic.title}」還沒有加入雲端目錄，請等註冊完成後再指派分類。`)
  }
  return comic.cloudComicId
}

/** 取得雲端分類並同步鏡像。回傳的計數涵蓋尚未備份的作品。 */
export async function loadCategories(client: CategoryReader = api): Promise<CloudCategory[]> {
  const { items } = await client.categories()
  await mirrorCategories(items)
  return items
}

export async function createCategory(name: string, isPrivate = false, client: CategoryClient = api): Promise<CloudCategory[]> {
  await client.createCategory(name.trim(), isPrivate)
  return loadCategories(client)
}

/** isPrivate 未指定時維持原值：更名與改隱私是兩件事，不該互相蓋掉。 */
export async function renameCategory(id: string, name: string, isPrivate?: boolean, client: CategoryClient = api): Promise<CloudCategory[]> {
  await client.renameCategory(id, name.trim(), isPrivate)
  return loadCategories(client)
}

export async function deleteCategory(id: string, client: CategoryClient = api): Promise<CloudCategory[]> {
  await client.deleteCategory(id)
  return loadCategories(client)
}

/**
 * 整理單本作品。
 *
 * 已註冊的作品先送出再更新鏡像。尚未註冊的作品在雲端沒有對應，標題與作者因此
 * 只寫本地——註冊時會帶上當下的值，不會分歧；分類沒有地方可存，所以不接受。
 * 這讓「還沒註冊完成」不至於連改個書名都做不到。
 */
export async function saveComicMetadata(
  comic: Comic, patch: { title: string; author: string; categoryIds: readonly string[] }, client: CategoryClient = api,
): Promise<void> {
  const title = patch.title.trim()
  if (!title) throw new Error('請填寫作品標題。')
  const author = patch.author.trim()
  const categoryIds = [...new Set(patch.categoryIds)]
  if (!isRegistered(comic)) {
    if (categoryIds.length) throw new Error(`「${comic.title}」還沒有加入雲端目錄，請等註冊完成後再指派分類。`)
    await mirrorComicMetadata(comic.id, title, author)
    return
  }
  const catalogId = catalogIdOf(comic)
  await client.updateComic(catalogId, { title, author, categoryIds: [...categoryIds] })
  await mirrorComicMetadata(comic.id, title, author)
  await mirrorComicCategories(comic.id, categoryIds)
}

export type CategoryAssignMode = 'add' | 'remove'

/**
 * 把選取切成單次請求裝得下的幾段。
 *
 * 段與段之間仍然是多次請求，因此那一層的「做到哪裡」仍然成立且必要；段內是一次原子
 * 請求，沒有中間狀態。作品數除以一百，實務上幾乎總是一段。
 */
export function bulkBatches<T>(items: readonly T[]): T[][] {
  const batches: T[][] = []
  for (let index = 0; index < items.length; index += BULK_ACTION_BATCH_SIZE) {
    batches.push(items.slice(index, index + BULK_ACTION_BATCH_SIZE))
  }
  return batches
}

/**
 * 對多本作品加入或移除分類。
 *
 * 集合運算在伺服器端完成，因此**不讀取每一本目前的指派**：原本的「先讀再寫」除了
 * 每本兩次往返，還在兩次請求之間留了一個窗口——期間那一本的分類若在別處變動，聯集
 * 算的是舊值，寫回去就把別人的變動蓋掉。
 *
 * 雲端那一段是全有全無，因此失敗時 completed 為 0：沒有「已完成幾本」可報。
 *
 * 鏡像那一段每一段也只提交一個交易：書庫清單訂閱著鏡像，逐本寫入會讓清單逐本重新發出。
 */
export async function assignCategories(
  comics: readonly Comic[], categoryIds: readonly string[], mode: CategoryAssignMode, client: CategoryClient = api,
): Promise<BulkOutcome> {
  const wanted = [...new Set(categoryIds)]
  const completedIds: string[] = []
  const registered = comics.filter(isRegistered)
  if (!registered.length || !wanted.length) return { completed: 0, completedIds, reason: '' }
  for (const batch of bulkBatches(registered)) {
    try {
      await client.bulkAssignCategories(batch.map(catalogIdOf), wanted, mode)
      await mirrorAssignmentsBatch(batch, wanted, mode)
    } catch (cause) {
      return { completed: completedIds.length, completedIds, reason: errorText(cause), cause }
    }
    completedIds.push(...batch.map(comic => comic.id))
  }
  return { completed: completedIds.length, completedIds, reason: '' }
}

/**
 * 批次編輯標題或作者。
 *
 * 值的真相在雲端目錄，因此已註冊的作品必須送出去——只寫本地會讓兩邊分歧，而分歧會在
 * 下一次從雲端載入清單時把使用者剛改好的值蓋回舊值。尚未註冊的作品只寫本地，其標題與
 * 作者會在註冊時一併帶上。
 *
 * 雲端那一段是一次請求、原子套用：任一值被拒就整段不套用，因此沒有半套用的狀態。
 */
export async function editComics(
  field: EditableField, updates: readonly { comic: Comic; value: string }[], client: CategoryClient = api,
): Promise<BulkOutcome> {
  const completedIds: string[] = []
  const trimmed = updates.map(({ comic, value }) => ({ comic, value: value.trim() }))
  // 標題不能是空的，作者可以。
  const empty = field === 'title' ? trimmed.find(item => !item.value) : undefined
  if (empty) return { completed: 0, completedIds, reason: `「${empty.comic.title}」套用後的標題是空的。` }

  for (const batch of bulkBatches(trimmed)) {
    const registered = batch.filter(item => isRegistered(item.comic))
    try {
      if (registered.length) {
        await client.bulkEdit(field, registered.map(item => ({ id: catalogIdOf(item.comic), value: item.value })))
      }
      for (const item of batch) {
        const title = field === 'title' ? item.value : item.comic.title
        const author = field === 'author' ? item.value : item.comic.author
        await mirrorComicMetadata(item.comic.id, title, author)
      }
    } catch (cause) {
      return { completed: completedIds.length, completedIds, reason: errorText(cause), cause }
    }
    completedIds.push(...batch.map(item => item.comic.id))
  }
  return { completed: completedIds.length, completedIds, reason: '' }
}
